import { call, put, takeLatest } from "redux-saga/effects";
import { UserEnrolledDevicesServiceFailure, UserEnrolledDevicesServiceInit, UserEnrolledDevicesServiceSuccess } from "./UserEnrolledDevicesSlice";
import { _API_ENDPOINT, LOCALSESSIONDATAKEYS, MAX_ENROLMENT_ALLOWED_PER_USER, SIGNUPTYPE, SUCCESS, USER_ENROLLED_SERVICE_LIST, ENROLLMENT_TYPE } from "../../constants/AppConstants";
import { getLocalSessionData, setLocalSessionData } from "../../utils/Authentication/handleUserSession";
import { handleError } from "../SagaError";
import i18next from "i18next";
import { _CALLGETAPI } from "../../api/apiHandler";
import { 
    findValueByKey_From_ArrayOfObj, 
    getShenlongQueryParams, 
    isDesktopOrBmcClient 
} from "../../constants/AppMethod";
import { nativeAppRoutePaths, routePath } from "../../constants/Routes";


function __GETUSERENROLLEDDEVICES() {
    const endpoint = _API_ENDPOINT.FETCH_USERS_ENROLLED_DEVICES_LIST;
    const UID = getLocalSessionData(LOCALSESSIONDATAKEYS.UID);

    const payload = {
        params: {
            'userId': UID
        }
    }
    return _CALLGETAPI(endpoint, payload)
        .then(response => {
            return response;
        })
        .catch((error) => {
            throw error;
        });
}

function* handleSuccess(enrolledDevicesList, newProp = null) {
    if (enrolledDevicesList?.userEnrolledDeviceViewModel?.length > 0) {
        var isEcoProDeviceAvailable = enrolledDevicesList?.userEnrolledDeviceViewModel.some((item) => {
            return (item.enrolledServices.includes(USER_ENROLLED_SERVICE_LIST.ECOPRO)) ? true : false;
        });
        if (isEcoProDeviceAvailable)
            setLocalSessionData(LOCALSESSIONDATAKEYS.ENROLLMENT_TYPE, ENROLLMENT_TYPE.ECOPRO);
        else
            setLocalSessionData(LOCALSESSIONDATAKEYS.ENROLLMENT_TYPE, ENROLLMENT_TYPE.BROTHER_PLUS);
    }
    yield put(UserEnrolledDevicesServiceSuccess({
        ...enrolledDevicesList,
        ...(newProp && newProp)
    }));
}

/**
 * Method to handle the device check for BMC Enrolment journey
 * @param {Array} enrolledDevicesList 
 * @param {boolean} hasReachedMaxEnrollment 
 * @return void
 */
function* handleDeviceExistCheck(enrolledDevicesList, hasReachedMaxEnrollment) {
    const enrolledDeviceListArr = enrolledDevicesList?.userEnrolledDeviceViewModel;
    const toBeEnrolledDeviceID = getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID);
    const isDeviceAvailableInResp = findValueByKey_From_ArrayOfObj(enrolledDeviceListArr, 'deviceId', toBeEnrolledDeviceID, 'deviceId');

    if (isDeviceAvailableInResp) {
        yield* handleSuccess(enrolledDevicesList, { 'isDashboard': true });
    } else {
        (hasReachedMaxEnrollment)
            ? yield* handleError(UserEnrolledDevicesServiceFailure, i18next.t('CUSTOMER_WEB.ERROR_CONTENT.SUBSCRIPTION_ALREADY_EXISTS'))
            : yield* handleSuccess(enrolledDevicesList);
    }
}

/**
 * Method to handle the device check for BMC Enrolment journey
 * @param {Array} enrolledDevicesList 
 * @param {boolean} hasReachedMaxEnrollment 
 * @return void
 */
function* handleDeviceExistCheckWithService(enrolledDevicesList, hasReachedMaxEnrollment) {
    const {params} = getShenlongQueryParams() || {};
    const parsedUrl = new URL(params);
    const toBeEnrolledService = (parsedUrl.pathname === nativeAppRoutePaths.BPLUS.DASHBOARD_PAGE || parsedUrl.pathname === routePath.DASHBOARD) ? USER_ENROLLED_SERVICE_LIST.BPLUS : USER_ENROLLED_SERVICE_LIST.ECOPRO
    const enrolledDeviceListArr = enrolledDevicesList?.userEnrolledDeviceViewModel;
    const toBeEnrolledDeviceID = getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID);
    const deviceEnrolledServiceInfo = findValueByKey_From_ArrayOfObj(enrolledDeviceListArr, 'deviceId', toBeEnrolledDeviceID, 'enrolledServices');
    const isDeviceAlreadyEnrolledToService = deviceEnrolledServiceInfo?.split(",").includes(toBeEnrolledService);
    if (isDeviceAlreadyEnrolledToService) {
        yield* handleSuccess(enrolledDevicesList, { 'isDashboard': true });
    } else {
        (hasReachedMaxEnrollment)
            ? yield* handleError(UserEnrolledDevicesServiceFailure, i18next.t('CUSTOMER_WEB.ERROR_CONTENT.SUBSCRIPTION_ALREADY_EXISTS'))
            : yield* handleSuccess(enrolledDevicesList);
    }
}

/** Method to handle signin/login redirection
 * - if user does not have an existing subscription, then redirect to signup journey
 * - if user has an existing EcoPro/BrotherPlus subscription, then redirect to customer dashboard
 * - (while redirecting to dashboard, if user has atleast one device subscribed to Ecopro then
 *    EcoPro's GetUserAccount API will be called. If user has devices enrolled to only BrotherPlus then
 *    BrotherPlus's getUserAccount API will be called by setting SIGNUP_TYPE to BROTHER_PLUS in local storage)
 */
function* handleLoginRedirectionCheck(enrolledDevicesList) {
    if (enrolledDevicesList?.userEnrolledDeviceViewModel?.length > 0) {
        var isEcoProDeviceAvailable = enrolledDevicesList?.userEnrolledDeviceViewModel.some((item) => {
            return (item.enrolledServices.includes(USER_ENROLLED_SERVICE_LIST.ECOPRO)) ? true : false;
        });
        var isBrotherPlusDeviceAvailabel = enrolledDevicesList?.userEnrolledDeviceViewModel.some((item) => {
            return (item.enrolledServices.includes(USER_ENROLLED_SERVICE_LIST.BPLUS)) ? true : false;
        });

        // set SIGNUP_TYPE to BROTHER_PLUS if user has only BrotherPlus enrolled devices
        if (!isEcoProDeviceAvailable && isBrotherPlusDeviceAvailabel) {
            setLocalSessionData(LOCALSESSIONDATAKEYS.SIGNUP_TYPE, SIGNUPTYPE.BROTHER_PLUS);
        }

        // redirect to customer dashboard
        yield* handleSuccess(enrolledDevicesList, { 'isDashboard': true });
    } else {

        // redirect to sign up journey
        yield* handleSuccess(enrolledDevicesList);
    }
}

function* handleEnrolledDevices(action) {
    try {
        const { doEnrolmentCheck, doDeviceExistCheck, doLoginRedirectionCheck } = action.payload || {};
        /** 
         * For native apps:
         *   - For EcoPro after device registration is completed in the app, the user must complete the enrollment process through login.
         *   - Therefore, doDeviceExistCheck validation is intentionally skipped.
         * */
        const isNativeApp = isDesktopOrBmcClient();
        const enrolledDevicesList = yield call(__GETUSERENROLLEDDEVICES);
        if (enrolledDevicesList.actionStatus.toUpperCase() === SUCCESS) {
            if (doEnrolmentCheck) {
                const deviceCount = enrolledDevicesList?.userEnrolledDeviceViewModel?.length;
                if (deviceCount === MAX_ENROLMENT_ALLOWED_PER_USER) {
                    (doDeviceExistCheck)
                        ? (isNativeApp) 
                            ? yield* handleDeviceExistCheckWithService(enrolledDevicesList) 
                            : yield* handleDeviceExistCheck(enrolledDevicesList, true)
                        : (doLoginRedirectionCheck)
                            ? yield* handleLoginRedirectionCheck(enrolledDevicesList)
                            : yield* handleError(UserEnrolledDevicesServiceFailure, i18next.t('CUSTOMER_WEB.ERROR_CONTENT.SUBSCRIPTION_ALREADY_EXISTS'));
                } else {
                    (doDeviceExistCheck)
                        ? (isNativeApp) 
                            ? yield* handleDeviceExistCheckWithService(enrolledDevicesList) 
                            : yield* handleDeviceExistCheck(enrolledDevicesList, false)
                        : (doLoginRedirectionCheck)
                            ? yield* handleLoginRedirectionCheck(enrolledDevicesList)
                            : yield* handleSuccess(enrolledDevicesList, (deviceCount > 1 && { 'skipAccountCreation': true }));
                }
            } else {
                yield* handleSuccess(enrolledDevicesList)
            }
        } else {
            yield* handleError(UserEnrolledDevicesServiceFailure, i18next.t("ERROR_CONTENT.ERR_INFO.TECHNICAL_ERROR"));
        }
    } catch (error) {
        yield* handleError(UserEnrolledDevicesServiceFailure, error.message);
    }
}

export default function* watchUserEnrolledDevicesService() {
    yield takeLatest(UserEnrolledDevicesServiceInit.type, handleEnrolledDevices)
}